import PropTypes from 'prop-types';
import { useState } from 'react';

import { trackAction } from '@ctct/distui-helpers';
import { Button, Dialog, Dropdown, IconButton, Menu, TableData, TableRow, Tag } from '@ctct/ui-react';

import AccountSvc from '../../../api/AccountSvc';
import displayLastUpdateTime from '../../../constants/flags';
import { getRoleName } from '../../../constants/userRoles';
import { SSO_INVITE_TYPE } from '../utils';

const propTypes = {
  target: PropTypes.string.isRequired,
  status: PropTypes.string.isRequired,
  roleId: PropTypes.number.isRequired,
  invitationType: PropTypes.string,
  expirationTime: PropTypes.string,
  t: PropTypes.func.isRequired,
  onRefreshInvites: PropTypes.func.isRequired,
  'data-qe-id': PropTypes.string.isRequired,
  setAlertData: PropTypes.func.isRequired,
  isLoggedInUserSiteOwner: PropTypes.bool.isRequired,
};

const defaultProps = {
  invitationType: null,
  expirationTime: null,
};

const InvitationRow = ({
  target,
  status,
  roleId,
  invitationType,
  expirationTime,
  t,
  'data-qe-id': qeId,
  onRefreshInvites,
  setAlertData,
  isLoggedInUserSiteOwner,
}) => {
  const [confirmResendDialogIsOpen, setConfirmResendDialogIsOpen] = useState(false);

  const onResendInvite = async () => {
    trackAction({ action: 'Resend invitation' });
    setConfirmResendDialogIsOpen(false);
    await AccountSvc.revokeInvitation(target);

    const invitationPayload = {
      target,
      targetType: 'EMAIL',
      roleId,
    };

    if (invitationType === SSO_INVITE_TYPE) {
      invitationPayload.invitationType = SSO_INVITE_TYPE;
    }

    await AccountSvc.postInvitation(invitationPayload);
    onRefreshInvites();
    setAlertData({ message: t('users.resentInvitationAlert', { target }) });
  };

  const onRevokeInvite = async () => {
    trackAction({ action: 'Revoke invitation' });
    await AccountSvc.revokeInvitation(target);
    onRefreshInvites();
    setAlertData({ message: t('users.revokedInvitationAlert', { target }) });
  };

  const onDeleteInvite = async () => {
    trackAction({ action: 'Delete invitation' });
    await AccountSvc.deleteInvitation(target);
    onRefreshInvites();
    setAlertData({ message: t('users.deletedInvitationAlert', { target }) });
  };

  const onMenuItemClicked = (ev, item) => {
    switch (item.value) {
      case 'revokeinvite':
        onRevokeInvite();
        break;
      case 'resendinvite':
        setConfirmResendDialogIsOpen(true);
        break;
      case 'deleteinvite':
        onDeleteInvite();
        break;
      default:
      // do nothing
    }
  };

  const role = getRoleName(roleId, t);

  const confirmResendInviteDialog = () => (
    <Dialog
      data-qe-id="dialog"
      heading={t('users.confirmResend.send_new_invite')}
      kind="prompt"
      isOpen={confirmResendDialogIsOpen}
      footerActions={
        <>
          <Button text={t('users.confirmResend.cancel')} onClick={() => setConfirmResendDialogIsOpen(false)} />
          <Button
            data-qe-id={`${qeId}-confirm-resend-button`}
            text={t('users.confirmResend.continue')}
            kind="primary"
            onClick={() => {
              onResendInvite();
            }}
          />
        </>
      }
      onDismiss={() => setConfirmResendDialogIsOpen(false)}
    >
      <b>{t('users.confirmResend.send_invite', { target })}</b>
      <br />
      <br />
      {t('users.confirmResend.will_revoke')}
      <br />
      <br />
    </Dialog>
  );

  let menuItems = [{ text: t('users.actions.resend_invite'), value: 'resendinvite' }];

  if (status !== 'REVOKED') {
    menuItems = menuItems.concat({ text: t('users.actions.revoke_invite'), value: 'revokeinvite' });
  }

  if (status === 'EXPIRED' || status === 'REVOKED') {
    menuItems = menuItems.concat({ text: t('users.actions.delete_invite'), value: 'deleteinvite' });
  }

  return (
    <TableRow>
      {confirmResendInviteDialog()}
      <TableData> - </TableData>
      <TableData data-qe-id={`${qeId}-email`}> {target}</TableData>
      <TableData>
        <Tag text={status} data-qe-id={`${qeId}-status`} />
      </TableData>
      <TableData data-qe-id={`${qeId}-role`}> {role}</TableData>
      <TableData data-qe-id={`${qeId}-date`}>
        {expirationTime ? t('users.expireson', { date: new Date(expirationTime).toLocaleString() }) : ''}
      </TableData>
      {displayLastUpdateTime && <TableData data-qe-id={`${qeId}-lastUpdate`} />}
      <TableData contentType="actions">
        {isLoggedInUserSiteOwner && (
          <Dropdown unmountedWhenClosed>
            <IconButton title="Actions" glyph="actions" onClick={() => {}} data-qe-id={`${qeId}-actions-button`} />
            <Menu data-qe-id={`${qeId}-menu`} items={menuItems} onChoose={onMenuItemClicked} />
          </Dropdown>
        )}
      </TableData>
    </TableRow>
  );
};

InvitationRow.propTypes = propTypes;
InvitationRow.defaultProps = defaultProps;
export default InvitationRow;
